import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'

import { Container, TimePlace } from './Common'
import LEBookingTag from '../../../Tripkit/Common/LEBookingTag'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import FullArrow from 'components/utils/Arrows/FullArrow'
import { DAY_MONTH_NAME } from 'constants/dateFormats'
import { useAppSelector } from 'hooks/reduxHooks'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import { isStandaloneFlightsEnabled } from 'selectors/flightsSelectors'
import SearchFlights from 'tripPlanner/components/TripModal/TripItems/Search/SearchFlights'
import TripItemSummary from 'tripPlanner/components/TripModal/TripItems/Summary'
import { useAirport } from 'tripPlanner/hooks/api'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import TripItemLabel from 'tripPlanner/Tripkit/Core/TripItemTile/Primitives/TripItemLabel'
import { FlightItem, SavedFlightSearchData } from 'tripPlanner/types/tripItem'
import {
  calculateFlightTimeLabel,
  isLeBooking,
  isLeBookmark,
} from 'tripPlanner/utils'
import { buildStandaloneFlightSearchURL } from 'tripPlanner/utils/bookmark/restoreCart'

const StyledContainer = styled(Container)`
  padding: ${rem(16)};
`

const Place = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${rem(4)};

  & > section:nth-child(odd) {
    display: flex;
    width: max-content;
    flex-direction: column;
  }

  & > section:nth-child(even) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`

interface Props {
  item: FlightItem
}

function ViewFlightSummary({ item }: Props) {
  const {
    airlineName,
    flightNumber,
    destinationAirportCode,
    originAirportCode,
    startDate,
    startTime,
    endDate,
    endTime,
    startUtcDateTime,
    endUtcDateTime,
  } = item

  const timeFormat = useRegionTimeFormat()
  const flightSearchUrl = isLeBookmark(item)
    ? buildStandaloneFlightSearchURL(item.savedItemData)
    : undefined

  const openModal = useOpenModal()

  const onClick = () =>
    isLeBookmark(item)
      ? window.open(flightSearchUrl, '_blank')
      : openModal(TripItemSummary, { item })

  return (
    <>
      <TripItemLabel label1={airlineName} label2={flightNumber} />
      <Place>
        <section>
          {startDate && (
            <BodyText variant="small">
              {startDate.format(DAY_MONTH_NAME)}
            </BodyText>
          )}
          <Heading variant="heading6">{originAirportCode}</Heading>
          {startTime && (
            <BodyText variant="small">{startTime.format(timeFormat)}</BodyText>
          )}
        </section>
        <section>
          {startTime && startDate && endTime && endDate && (
            <Caption variant="large">
              {calculateFlightTimeLabel(startUtcDateTime, endUtcDateTime)}
            </Caption>
          )}
          <FullArrow arrowSize={16} width="100%" minWidth="100px" />
        </section>
        <section>
          {endDate && (
            <BodyText variant="small">
              {endDate.format(DAY_MONTH_NAME)}
            </BodyText>
          )}
          <Heading variant="heading6">{destinationAirportCode}</Heading>
          {endTime && (
            <BodyText variant="small">{endTime.format(timeFormat)}</BodyText>
          )}
        </section>
      </Place>
      {isLeBooking(item) && <LEBookingTag />}
      <TextButton kind="primary" onClick={onClick}>
        {isLeBookmark(item) ? 'View Search' : 'View Details'}
      </TextButton>
    </>
  )
}

function FindFlights({ item }: Props) {
  const {
    destinationAirportCode,
    originAirportCode,
    savedItemData = {} as SavedFlightSearchData,
  } = item
  const standaloneFlightsEnabled = useAppSelector(isStandaloneFlightsEnabled)
  const { fareType } = savedItemData
  const openModal = useOpenModal()

  const { data: departureAirport } = useAirport({ code: originAirportCode })
  const { data: arrivalAirport } = useAirport({
    code: destinationAirportCode,
  })

  const onClick = () => {
    openModal(SearchFlights, {
      tripId: item.tripId,
      prefillData: {
        departureAirport,
        arrivalAirport,
        fareType,
      },
    })
  }

  return (
    <>
      <TimePlace>
        <section>
          <Heading variant="heading6">{originAirportCode}</Heading>
          {departureAirport && (
            <BodyText variant="small">{departureAirport.cityDesc}</BodyText>
          )}
        </section>
        <section>
          <FullArrow arrowSize={14} width="100%" minWidth="100px" />
        </section>
        <section>
          <Heading variant="heading6">{destinationAirportCode}</Heading>
          {arrivalAirport && (
            <BodyText variant="small">{arrivalAirport.cityDesc}</BodyText>
          )}
        </section>
      </TimePlace>
      {standaloneFlightsEnabled && (
        <TextButton kind="secondary" fit="full-width" onClick={onClick}>
          Find flights
        </TextButton>
      )}
    </>
  )
}

const FlightSummaryCard = React.forwardRef<HTMLDivElement, Props>(
  ({ item }: Props, ref) => {
    const { startDate, endDate } = item

    return (
      <StyledContainer ref={ref}>
        <VerticalSpacer gap={8}>
          {startDate && endDate ? (
            <ViewFlightSummary item={item} />
          ) : (
            <FindFlights item={item} />
          )}
        </VerticalSpacer>
      </StyledContainer>
    )
  },
)

export default FlightSummaryCard
